import CloseIcon from '@mui/icons-material/Close';
import { IconButton, ThemeProvider, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CASTDialogContext from 'components/shared/modals/CASTDialog/CASTDialogContext';
import {
  CASTDialogOptions,
  CASTDialogProps,
  CASTDialogProviderProps,
} from 'components/shared/modals/CASTDialog/types';
import { ThemeContext } from 'context/themes';
import { useCallback, useContext, useState } from 'react';

const DEFAULT_OPTIONS: CASTDialogOptions = {
  title: null,
  subTitle: null,
  okText: 'Ok',
  cancelText: 'Cancel',
  dialogProps: {},
  dialogActionsProps: {},
  okButtonProps: {},
  cancelButtonProps: {},
  titleProps: {},
  contentProps: {},
  iconButtonProps: {},
  hideCancelButton: false,
  hideActions: false,
  buttonOrder: ['cancel', 'ok'],
};

const buildOptions = (options: CASTDialogProps): CASTDialogProps => {
  const dialogProps = {
    ...DEFAULT_OPTIONS.dialogProps,
    ...(options?.dialogProps || {}),
  };
  const dialogActionsProps = {
    ...DEFAULT_OPTIONS.dialogActionsProps,
    ...(options?.dialogActionsProps || {}),
  };
  const okButtonProps = {
    ...DEFAULT_OPTIONS.okButtonProps,
    ...(options?.okButtonProps || {}),
  };
  const cancelButtonProps = {
    ...DEFAULT_OPTIONS.cancelButtonProps,
    ...(options?.cancelButtonProps || {}),
  };
  const titleProps = {
    ...DEFAULT_OPTIONS.titleProps,
    ...(options?.titleProps || {}),
  };
  const contentProps = {
    ...DEFAULT_OPTIONS.contentProps,
    ...(options?.contentProps || {}),
  };
  const componentProps = {
    ...(options?.props || {}),
  };
  const iconButtonProps = {
    ...DEFAULT_OPTIONS.iconButtonProps,
    ...(options?.iconButtonProps || {}),
  };

  return {
    ...DEFAULT_OPTIONS,
    ...options,
    Component: options?.Component ?? (() => <></>),
    props: componentProps,
    dialogProps,
    dialogActionsProps,
    okButtonProps,
    cancelButtonProps,
    titleProps,
    contentProps,
    iconButtonProps,
  };
};

export type DialogResolution<R = unknown> = {
  ok: boolean;
  data?: R | undefined;
};

type ActiveDialog = {
  resolver: (resolution: DialogResolution) => void;
  options: CASTDialogProps;
  actionsNodeRef?: any;
};

const CASTDialogProvider = ({ children }: CASTDialogProviderProps) => {
  const { theme } = useContext(ThemeContext);

  const [dialogs, setDialogs] = useState<ActiveDialog[]>([]);

  const showDialog = (options: CASTDialogProps) => {
    return new Promise<any>((resolve) => {
      setDialogs((currentDialogs) => {
        return [
          ...currentDialogs,
          {
            resolver: resolve,
            options,
          },
        ];
      });
    });
  };

  const handleCancel = () => {
    setDialogs((currentDialogs) => {
      const activeDialog = currentDialogs[currentDialogs.length - 1];
      if (activeDialog) {
        activeDialog.resolver({ ok: false });
      }
      return currentDialogs.slice(0, currentDialogs.length - 1);
    });
  };

  const handleOk = (data) => {
    setDialogs((currentDialogs) => {
      const activeDialog = currentDialogs[currentDialogs.length - 1];
      if (activeDialog) {
        activeDialog.resolver({ ok: true, data });
      }
      return currentDialogs.slice(0, currentDialogs.length - 1);
    });
  };

  return (
    <>
      <CASTDialogContext.Provider
        value={{
          showDialog,
          cancelDialog: handleCancel,
          confirmDialog: handleOk,
        }}
      >
        <>
          {children}
          <ThemeProvider theme={theme}>
            {dialogs.map((dialog) => {
              const opts = buildOptions(dialog.options);
              return <CASTDialog {...opts} onOK={handleOk} onCancel={handleCancel} />;
            })}
          </ThemeProvider>
        </>
      </CASTDialogContext.Provider>
    </>
  );
};

const CASTDialog = ({
  Component,
  props,
  title,
  subTitle,
  dialogProps,
  dialogActionsProps,
  titleProps,
  iconButtonProps,
  contentProps,
  okText,
  okButtonProps,
  cancelText,
  cancelButtonProps,
  hideCancelButton,
  hideActions,
  buttonOrder,
  onOK,
  onCancel,
}: CASTDialogProps & { onOK: (data) => void; onCancel: () => void }) => {
  const [dialogActionsNode, setDialogActionsNode] = useState(null);
  const updateDialogActionsRef = useCallback((node) => {
    setDialogActionsNode(node);
  }, []);

  const dialogActions = buttonOrder.map((buttonType) => {
    if (buttonType === 'cancel') {
      return (
        !hideCancelButton && (
          <Button key="cancel" variant="outlined" {...cancelButtonProps} onClick={onCancel}>
            {cancelText}
          </Button>
        )
      );
    }

    if (buttonType === 'ok') {
      return (
        <Button key="ok" variant="contained" color="primary" {...okButtonProps} onClick={onOK}>
          {okText}
        </Button>
      );
    }

    throw new Error(`Supported button types are only "ok" and "cancel", got: ${buttonType}`);
  });

  const isFormComponent = !!props.onSubmit;

  return (
    <Dialog fullWidth maxWidth="md" {...dialogProps} open={true} onClose={onCancel}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }} {...titleProps}>
        <span>
          {title ?? ''}
          {subTitle && <Typography variant="subtitle2">{subTitle}</Typography>}
        </span>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          {...iconButtonProps}
          sx={{
            ml: 'auto',
            color: (theme) => theme.palette.grey[500],
            ...iconButtonProps?.sx,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent {...contentProps}>
        <Component
          dialogActionsNode={dialogActionsNode}
          {...props}
          onSubmit={
            isFormComponent &&
            (async (args) => {
              const returnVal = await props.onSubmit(args);
              onOK(returnVal);
            })
          }
        />
      </DialogContent>
      {!hideActions && (
        <DialogActions
          sx={{ px: isFormComponent ? 2 : 1 }}
          {...dialogActionsProps}
          ref={updateDialogActionsRef}
        >
          {!isFormComponent && dialogActions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CASTDialogProvider;
